import { atom } from 'jotai';
import { atomsWithQuery } from 'jotai-tanstack-query';
import { V2 } from '@icecreamsocial/api-resources';
import { publicKeyAtom } from '../publicKey';
import { focusAtom } from 'jotai-optics';
import { userEmailConsentAtom, userSMSConsentAtom } from '../user';
import { flags } from '../../../utils/featureFlags';

/**
 * @featureflags
 */
const ENABLE_EXPERIMENTAL_CONSENT_STEP = flags.isActive(
	flags.names.ENABLE_EXPERIMENTAL_CONSENT_STEP
);

type Campaign = {
	id: string;
	offerHeading: string;
	offerInstructions: string;
	termsAndConditionsURL: string;
	privacyPolicyURL: string;
	useEmail: boolean;
	useEmailConsent: boolean;
	useSMS: boolean;
	useSMSConsent: boolean;
	discountCodeDesc: string | null;
	locale: string;
};
/**
 * stores the campaign id
 */
export const campaignIdAtom = atom<string>('');

/**
 * Whether or not the user has opted in to the campaign
 */
export const optedInAtom = atom(false);

/**
 * gets the campaign
 */
export const [campaignAtom, campaignStatusAtom] = atomsWithQuery((get) => ({
	queryKey: ['campaign', get(campaignIdAtom), get(publicKeyAtom)],
	queryFn: async ({ queryKey: [, campaignId, publicKey] }) => {
		const { data } = await V2.Client.getCampaignInformation({
			campaignId,
			publicKey,
		});
		return data?.data;
	},
	enabled: !!get(campaignIdAtom) && !!get(publicKeyAtom),
}));

/**
 * gets the campaign discount code
 */
export const [discountCodeAtom, discountStatusAtom] = atomsWithQuery((get) => ({
	queryKey: [
		'campaign',
		'discountCode',
		get(campaignIdAtom),
		get(publicKeyAtom),
	],
	queryFn: async ({ queryKey: [, , campaignId, publicKey] }) => {
		const { data } = await V2.Client.getCampaignDiscounts({
			campaignId,
			publicKey,
		});
		return data?.data;
	},
	enabled: get(optedInAtom),
}));

/**
 * derived atoms
 */
export interface RequiredConsent {
	email: boolean;
	sms: boolean;
}

/**
 * consent atom
 */
export const requiredEmailConsentAtom = focusAtom(campaignStatusAtom, (optic) =>
	optic
		.prop('data')
		.prop('campaign')
		.prop('useEmailConsent')
);

export const requiredSMSConsentAtom = focusAtom(campaignStatusAtom, (optic) =>
	optic
		.prop('data')
		.prop('campaign')
		.prop('useSMSConsent')
);

export const requiredConsentAtom = atom((get) => ({
	email: get(requiredEmailConsentAtom) && !get(userEmailConsentAtom),
	sms: ENABLE_EXPERIMENTAL_CONSENT_STEP
		? get(requiredSMSConsentAtom) && !get(userSMSConsentAtom)
		: false,
}));

/**
 * read only consent
 */
export const anyConsentRequiredAtom = atom(
	(get) => get(requiredConsentAtom).email || get(requiredConsentAtom).sms
);
